import React, { useState, useEffect } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import './App.css';

function App() {
  const [temperatures, setTemperatures] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [error, setError] = useState('');

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [tempRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/temperatures`),
        axios.get(`${API_URL}/stats`)
      ]);
      setTemperatures(tempRes.data);
      setStats(statsRes.data);
      setError('');
    } catch (err) {
      setError('Erreur lors du chargement des données');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const filteredData = filter
    ? temperatures.filter(t =>
        t.department_number.toString().includes(filter) ||
        t.department_name.toLowerCase().includes(filter.toLowerCase())
      )
    : temperatures;

  const exportToExcel = () => {
    const ws = XLSX.utils.json_to_sheet(filteredData.map(t => ({
      'Département': t.department_name,
      'N° Département': t.department_number,
      'Temp. Max (°C)': t.max_temp,
      'Date': new Date(t.recorded_date).toLocaleDateString('fr-FR'),
      'Heure': t.recorded_time
    })));
    
    ws.A1.s = { font: { bold: true, color: { rgb: "FFFFFF" } }, fill: { fgColor: { rgb: "4472C4" } } };
    
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Températures');
    XLSX.writeFile(wb, `meteo_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const exportToCSV = () => {
    const csv = [
      ['Département', 'N° Département', 'Temp. Max (°C)', 'Date', 'Heure'],
      ...filteredData.map(t => [
        t.department_name,
        t.department_number,
        t.max_temp,
        new Date(t.recorded_date).toLocaleDateString('fr-FR'),
        t.recorded_time
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `meteo_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  return (
    <div className="container">
      <header className="header">
        <h1>🌡️ Météo France</h1>
        <p>Températures maximales par département (90 derniers jours)</p>
      </header>

      {error && <div className="error">{error}</div>}

      {stats && (
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-label">Départements</div>
            <div className="stat-value">{stats.total_departments}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Enregistrements</div>
            <div className="stat-value">{stats.total_records}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Temp. Max</div>
            <div className="stat-value">{stats.max_temperature}°C</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Temp. Moyenne</div>
            <div className="stat-value">{stats.avg_temperature}°C</div>
          </div>
        </div>
      )}

      <div className="controls">
        <input
          type="text"
          placeholder="Filtrer par département ou numéro..."
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="search-input"
        />
        <button onClick={exportToExcel} className="btn btn-primary">
          📊 Excel
        </button>
        <button onClick={exportToCSV} className="btn btn-secondary">
          📄 CSV
        </button>
        <button onClick={fetchData} className="btn btn-refresh">
          🔄 Actualiser
        </button>
      </div>

      {loading ? (
        <div className="loading">Chargement des données...</div>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Département</th>
                <th>N°</th>
                <th>Temp. Max (°C)</th>
                <th>Date</th>
                <th>Heure</th>
              </tr>
            </thead>
            <tbody>
              {Array.isArray(filteredData) && filteredData.length > 0 ? (
                filteredData.map((temp, idx) => (
                  <tr key={idx}>
                    <td>{temp.department_name}</td>
                    <td className="dept-num">{temp.department_number}</td>
                    <td className="temp">
                      <span className={`temp-badge ${temp.max_temp > 25 ? 'hot' : 'cool'}`}>
                        {temp.max_temp}°
                      </span>
                    </td>
                    <td>{new Date(temp.recorded_date).toLocaleDateString('fr-FR')}</td>
                    <td>{temp.recorded_time}</td>
                  </tr>
                ))
              ) : (
                <tr><td colSpan="5" className="no-data">Aucune donnée</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <footer className="footer">
        <p>Données accessibles via API REST • Base de données PostgreSQL</p>
      </footer>
    </div>
  );
}

export default App;
